/*
 * <cart-recommendations> — "you may also like" row for the cart drawer.
 *
 * Content strategy (light by design):
 *   1. PRIMARY: best-sellers collection — server-rendered in the drawer Liquid
 *      (gender-filtered + cart-deduped there). Zero JS cost.
 *   2. BLEND: when the drawer opens, fetch Shopify's native related-products
 *      API once (cached per seed) and prepend up to data-blend picks that are
 *      NOT near-duplicates of cart items (drops same-model colorways) and not
 *      already shown.
 * Row navigation: prev/next arrows scroll the CSS scroll-snap list (no Swiper
 * instance for the row). Card image sliders (multi-image products) are
 * initialized via window.initProductCardSliders once the drawer opens.
 * Re-render-safe: the drawer re-parses this element on every cart change.
 */
(function () {
	if (customElements.get('cart-recommendations')) return;

	var FETCH_CACHE = {}; // url -> array of candidate <li> outerHTML

	function normalizeTitle(t) {
		return (t || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, ' ')
			.replace(/\s+/g, ' ')
			.trim();
	}

	/* Colorway duplicates always share the garment noun ("Fleece Hoodie Black"
	   vs "Fleece Hoodie Heather Gray") — different nouns (hoodie vs sweatpant)
	   mean a set piece worth recommending, not a duplicate. Footwear titles
	   carry no garment noun and fall through to the token check unchanged. */
	var GARMENT_NOUNS = ['hoodie', 'tee', 'shirt', 'sweatshirt', 'sweater', 'crewneck', 'sweatpant', 'pant', 'legging', 'sweatshort', 'short', 'jacket', 'coat', 'vest', 'cap', 'hat', 'beanie', 'sock', 'jean'];

	function garmentNoun(tokens) {
		for (var i = 0; i < tokens.length; i++) {
			for (var j = 0; j < GARMENT_NOUNS.length; j++) {
				if (tokens[i] === GARMENT_NOUNS[j] || tokens[i] === GARMENT_NOUNS[j] + 's') return GARMENT_NOUNS[j];
			}
		}
		return '';
	}

	/* Same-model detector. Card titles may drop the vendor prefix ("Jordan 1
	   Retro…" renders as "1 Retro…"), so positional comparison fails — use
	   token-SET overlap of significant tokens instead. */
	function isNearDuplicate(candidate, cartTitles) {
		var c = normalizeTitle(candidate);
		if (!c) return false;
		var cToks = c.split(' ');
		var cNoun = garmentNoun(cToks);
		var cSet = {};
		cToks.forEach(function (tok) { if (tok.length > 1 || /\d/.test(tok)) cSet[tok] = true; });
		for (var i = 0; i < cartTitles.length; i++) {
			var t = cartTitles[i];
			if (!t) continue;
			var tToks = t.split(' ');
			if (cNoun) {
				var tNoun = garmentNoun(tToks);
				if (tNoun && tNoun !== cNoun) continue;
			}
			if (c.indexOf(t) !== -1 || t.indexOf(c) !== -1) return true;
			var shared = 0, total = 0;
			for (var j = 0; j < tToks.length; j++) {
				var tok = tToks[j];
				if (tok.length <= 1 && !/\d/.test(tok)) continue;
				total++;
				if (cSet[tok]) shared++;
			}
			if (shared >= 3 || (total > 0 && shared / total >= 0.6)) return true;
		}
		return false;
	}

	/* Shoe-numeric size detector (mirrors the Liquid `plus: 0` leading-digit
	   parse). Footwear here carries variants ONLY for stocked sizes, so a
	   shoe-numeric candidate missing a shoe-numeric seed size can't fit the
	   shopper — apparel (S/M/L) parses to NaN and stays unconstrained. */
	function isShoeSize(s) {
		var n = parseFloat(s);
		return n > 0 && n <= 20;
	}

	/* Blend picks can't read metafields client-side — cheap gender heuristic
	   from title markers so kids/GS items don't land in an adult cart. */
	function genderMismatch(title, gender) {
		if (!gender) return false;
		if (/\b(gs|ps|td)\b|\(gs\)|\(ps\)|\(td\)|toddler|little kids/i.test(title)) return gender !== 'kids';
		if (/\bwomen'?s?\b|\bwmns\b/i.test(title)) return gender === 'men';
		return false;
	}

	class CartRecommendations extends HTMLElement {
		constructor() {
			super();
			this.limit = parseInt(this.dataset.limit, 10) || 6;
			this.blend = parseInt(this.dataset.blend, 10) || 0;
			this.target = this.querySelector('[data-cart-rec-target]');
			this.booted = false;
		}

		connectedCallback() {
			/* The drawer swaps its inner HTML on every cart change, but ModalDialog
			   self-moves each quick-add modal to <body> on connect — the previous
			   render's modals would pile up there forever (duplicate IDs). On
			   re-renders this fires before our descendant modals upgrade/move
			   (tree order), so `body >` matches strays. ⚠️ On the INITIAL page
			   parse the order flips: quick-add.js defines first and moves THIS
			   render's modals to <body> before we ever run — so keep any body
			   modal that belongs to a current card and has no fresh copy inside
			   this element awaiting its own move. */
			var self = this;
			var cardIds = {};
			this.querySelectorAll('li[data-product-id]').forEach(function (li) {
				cardIds['QuickAdd-cartrec-' + li.dataset.productId] = true;
			});
			document.querySelectorAll('body > quick-add-modal[id^="QuickAdd-cartrec-"]:not([open])').forEach(function (el) {
				var freshCopyInside = self.querySelector('quick-add-modal[id="' + el.id + '"]');
				if (freshCopyInside || !cardIds[el.id]) el.remove();
			});
			if (!this.target) return;
			this.wireArrows();
			// Boot when the drawer opens — keyed off <cart-drawer>'s `.active`
			// class (display-independent; this element may start hidden/off-canvas).
			var drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
			if (!drawer) { this.boot(); return; }
			if (drawer.classList.contains('active')) { this.boot(); return; }
			this.observer = new MutationObserver(() => {
				if (drawer.classList.contains('active')) {
					this.observer.disconnect();
					this.boot();
				}
			});
			this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
		}

		disconnectedCallback() {
			if (this.observer) this.observer.disconnect();
		}

		boot() {
			if (this.booted) return;
			this.booted = true;
			this.pruneServerDuplicates();
			this.initCardSliders();
			this.syncArrows();
			if (this.blend > 0 && this.dataset.url && this.dataset.seed) this.blendRelated();
			this.finalize();
		}

		list() {
			return this.querySelector('.cart-recommendations__list');
		}

		/* The Liquid loop can't run the token-overlap filter, so server-rendered
		   cards may be colorways of cart items ("you may also like: the thing you
		   just added, in grey") — drop them at boot with the same detector the
		   blend uses. Runs before blendRelated so its dedupe reads the pruned DOM. */
		pruneServerDuplicates() {
			var listEl = this.list();
			if (!listEl) return;
			var cartTitles = (this.dataset.cartTitles || '').split('||').map(normalizeTitle);
			listEl.querySelectorAll(':scope > li').forEach(function (li) {
				var titleEl = li.querySelector('.card__title');
				if (titleEl && isNearDuplicate(titleEl.textContent.trim(), cartTitles)) li.remove();
			});
		}

		/* ---- blend: related API picks that add variety ---- */
		blendRelated() {
			var url = this.dataset.url;
			var apply = (candidates) => this.applyBlend(candidates);
			if (FETCH_CACHE[url]) { apply(FETCH_CACHE[url]); return; }
			fetch(url)
				.then(function (r) { return r.text(); })
				.then((text) => {
					var doc = new DOMParser().parseFromString(text, 'text/html');
					var items = doc.querySelectorAll('[data-cart-rec-items] > li');
					var out = [];
					for (var i = 0; i < items.length; i++) {
						var titleEl = items[i].querySelector('.card__title');
						out.push({
							id: items[i].dataset.productId || '',
							title: titleEl ? titleEl.textContent.trim() : '',
							vendor: items[i].dataset.vendor || '',
							sizesAll: items[i].dataset.sizesAll || '',
							sizesAvailable: items[i].dataset.sizesAvailable || '',
							html: items[i].outerHTML
						});
					}
					FETCH_CACHE[url] = out;
					apply(out);
				})
				.catch(() => { this.finalize(); });
		}

		applyBlend(candidates) {
			var listEl = this.list();
			if (!listEl) return;
			var inCart = {};
			(this.dataset.cartProducts || '').split(',').forEach(function (id) { if (id) inCart[id] = true; });
			var shown = {};
			listEl.querySelectorAll('[data-product-id]').forEach(function (li) { shown[li.dataset.productId] = true; });
			var cartTitles = (this.dataset.cartTitles || '').split('||').map(normalizeTitle);
			var gender = this.dataset.gender || '';
			var seedSize = this.dataset.seedSize || '';
			var seedShoe = this.dataset.seedShoe === 'true';
			var vendorCap = parseInt(this.dataset.vendorCap, 10) || 0;
			var vendorCount = {};
			listEl.querySelectorAll(':scope > li[data-vendor]').forEach(function (li) {
				var v = li.dataset.vendor;
				if (v) vendorCount[v] = (vendorCount[v] || 0) + 1;
			});

			var blend = this.blend;
			var picks = [];
			var isShoeCand = function (cand) {
				return !!cand.sizesAll && cand.sizesAll.split('|').some(isShoeSize);
			};
			var consider = function (cand) {
				if (picks.length >= blend) return;
				if (!cand.id || inCart[cand.id] || shown[cand.id]) return;
				// The related API can surface utility "products" (Navidium order
				// protection, gift cards) — never recommend those.
				if (/order protection|shipping protection|gift card/i.test(cand.title)) return;
				if (isNearDuplicate(cand.title, cartTitles)) return;
				if (genderMismatch(cand.title, gender)) return;
				// Size filter: only constrains candidates sharing the seed's size
				// system (apparel vs shoe sizing passes untouched). If the candidate
				// carries the seed size it must be in stock; if it's shoe-numeric
				// like the seed but MISSING the size, it can't fit the shopper.
				if (seedSize && cand.sizesAll) {
					var all = cand.sizesAll.split('|');
					if (all.indexOf(seedSize) !== -1) {
						if (('|' + cand.sizesAvailable).indexOf('|' + seedSize + '|') === -1) return;
					} else if (isShoeSize(seedSize) && all.some(isShoeSize)) {
						return;
					}
				}
				if (vendorCap > 0 && cand.vendor && (vendorCount[cand.vendor] || 0) >= vendorCap) return;
				if (cand.vendor) vendorCount[cand.vendor] = (vendorCount[cand.vendor] || 0) + 1;
				shown[cand.id] = true;
				picks.push(cand);
			};
			// Shoe in the cart = footwear shopping intent — shoe candidates get
			// first claim on the blend slots; anything left fills the remainder.
			if (seedShoe) candidates.filter(isShoeCand).forEach(consider);
			candidates.forEach(consider);

			// With a shoe seed the server row leads with footwear — insert picks
			// after the last footwear card so apparel picks don't jump the queue.
			var anchor = null;
			if (seedShoe) {
				listEl.querySelectorAll(':scope > li').forEach(function (li) {
					if ((li.dataset.sizesAll || '').split('|').some(isShoeSize)) anchor = li;
				});
			}
			// Insert in reverse so the first-picked candidate ends up first.
			for (var i = picks.length - 1; i >= 0; i--) {
				if (anchor) anchor.insertAdjacentHTML('afterend', picks[i].html);
				else listEl.insertAdjacentHTML('afterbegin', picks[i].html);
			}
			// keep the row at the configured length
			var cards = listEl.querySelectorAll(':scope > li');
			for (var k = cards.length - 1; k >= this.limit; k--) cards[k].remove();
			if (picks.length) {
				listEl.scrollLeft = 0; // prepending shifts the scroll position — snap back to the start
				this.initCardSliders();
				if (typeof colorSwatches === 'function') { try { colorSwatches(); } catch (e) {} }
			}
			this.finalize();
		}

		finalize() {
			var listEl = this.list();
			var count = listEl ? listEl.querySelectorAll(':scope > li').length : 0;
			this.hidden = count === 0;
			this.syncArrows();
		}

		initCardSliders() {
			if (typeof window.initProductCardSliders === 'function') {
				window.initProductCardSliders(this);
			}
		}

		/* ---- row arrows: scroll the snap list, no slider library ---- */
		wireArrows() {
			var prev = this.querySelector('.cart-recommendations__arrow--prev');
			var next = this.querySelector('.cart-recommendations__arrow--next');
			if (!prev || !next) return;
			var step = () => {
				var listEl = this.list();
				var card = listEl && listEl.querySelector(':scope > li');
				return card ? card.getBoundingClientRect().width + 12 : 160;
			};
			prev.addEventListener('click', () => { var l = this.list(); if (l) l.scrollBy({ left: -step(), behavior: 'smooth' }); });
			next.addEventListener('click', () => { var l = this.list(); if (l) l.scrollBy({ left: step(), behavior: 'smooth' }); });
			var pending = false;
			this.addEventListener('scroll', () => {
				if (pending) return;
				pending = true;
				requestAnimationFrame(() => { pending = false; this.syncArrows(); });
			}, true);
		}

		syncArrows() {
			var prev = this.querySelector('.cart-recommendations__arrow--prev');
			var next = this.querySelector('.cart-recommendations__arrow--next');
			var listEl = this.list();
			if (!prev || !next || !listEl) return;
			var max = listEl.scrollWidth - listEl.clientWidth;
			prev.disabled = listEl.scrollLeft <= 2;
			next.disabled = listEl.scrollLeft >= max - 2;
			var overflow = max > 4;
			prev.style.display = overflow ? '' : 'none';
			next.style.display = overflow ? '' : 'none';
			var countEl = this.querySelector('[data-cart-rec-count]');
			if (countEl) {
				var cards = listEl.querySelectorAll(':scope > li');
				if (cards.length && cards[0].getBoundingClientRect().width > 0) {
					var cardW = cards[0].getBoundingClientRect().width + 8; // 8px = list gap
					var idx = Math.min(cards.length, Math.round(listEl.scrollLeft / cardW) + 1);
					countEl.textContent = idx + '/' + cards.length;
				} else {
					countEl.textContent = '';
				}
			}
		}
	}

	customElements.define('cart-recommendations', CartRecommendations);
})();
